import { customElement } from 'lit/decorators.js'
import { html, LitElement, nothing } from 'lit'
import { CSCDeliveryMatchMethodGroup } from './delivery-match-method-group.js'
import { DeliveryMethods, DropoffMethods, type DeliveryMethod } from './api/shipment-methods.js'
import { hostStyle } from './delivery-match-methods.style.js'
import { consume } from '@lit/context'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { DeliveryMatchActionsContext, DeliveryMatchCurrencyContext, DeliveryMatchDeliveryMethodsContext, DeliveryMatchDropoffMethodsContext, DeliveryMatchErrorContext, DeliveryMatchIsLoadingMethodsContext, DeliveryMatchMethodsByCategoriesContext, DeliveryMatchSelectedMethodGroupContext, NO_SELECTED_METHOD_GROUP, type DeliveryMatchActionsContextType, type DeliveryMatchCurrencyContextType, type DeliveryMatchDeliveryMethodsContextType, type DeliveryMatchDropoffMethodsContextType, type DeliveryMatchErrorContextType, type DeliveryMatchMethodsByCategoriesContextType, type DeliveryMatchSelectedMethodGroupContextType, type DeliveryMethodGroupType } from './provider.js'
import { getRelativeDateInfo } from './utils.js'
import { map } from 'lit/directives/map.js'
import { is } from 'typia'

@customElement('csc-delivery-match-methods')
export class CSCDeliveryMatchMethods extends LitElement {
  showRadioButtons: boolean = false
  showIcons: boolean = false
  inline: boolean = false

  static override styles = [hostStyle]

  @consume({ context: DeliveryMatchActionsContext })
  private actions!: DeliveryMatchActionsContextType

  @consume({ context: DeliveryMatchErrorContext, subscribe: true })
  private error!: DeliveryMatchErrorContextType

  @consume({ context: DeliveryMatchSelectedMethodGroupContext, subscribe: true })
  selectedMethodGroup!: DeliveryMatchSelectedMethodGroupContextType

  @consume({ context: DeliveryMatchCurrencyContext })
  currencyContext!: DeliveryMatchCurrencyContextType

  @consume({ context: DeliveryMatchIsLoadingMethodsContext, subscribe: true })
  isLoadingMethods!: boolean

  @consume({ context: DeliveryMatchDeliveryMethodsContext, subscribe: true })
  private deliveryMethods!: DeliveryMatchDeliveryMethodsContextType

  @consume({ context: DeliveryMatchDropoffMethodsContext, subscribe: true })
  private dropoffMethods!: DeliveryMatchDropoffMethodsContextType

  @consume({ context: DeliveryMatchMethodsByCategoriesContext, subscribe: true })
  private methodsByCategories!: DeliveryMatchMethodsByCategoriesContextType

  constructor({
    showRadioButtonsGroup,
    showIconsGroup,
    inline,
  }: {
    showRadioButtonsGroup?: boolean
    showIconsGroup?: boolean
    inline?: boolean
  }) {
    super()
    this.showRadioButtons = showRadioButtonsGroup ?? false
    this.showIcons = showIconsGroup ?? false
    this.inline = inline ?? false
  }

  protected override updated() {
    const totalDeliveryMethodsCount = this.methodsByCategories.filter(m => is<DeliveryMethods>(m))?.[0]?.methods.length
    const totalDropoffMethodsCount = this.methodsByCategories.filter(m => is<DropoffMethods>(m))?.[0]?.methods.length

    if (totalDeliveryMethodsCount && !totalDropoffMethodsCount) {
      this.actions.setSelectedMethodGroup('delivery')
    } else if (totalDropoffMethodsCount && !totalDeliveryMethodsCount) {
      this.actions.setSelectedMethodGroup('dropoff')
    }
  }

  private handleStepClick(_: Event, groupType: DeliveryMethodGroupType) {
    this.actions.setSelectedMethodGroup(groupType)
  }

  private isSelected = (groupType: DeliveryMethodGroupType) => {
    const totalDeliveryMethodsCount = this.methodsByCategories.filter(m => is<DeliveryMethods>(m))?.[0]?.methods.length
    const totalDropoffMethodsCount = this.methodsByCategories.filter(m => is<DropoffMethods>(m))?.[0]?.methods.length

    if (this.selectedMethodGroup == NO_SELECTED_METHOD_GROUP) {
      if (totalDeliveryMethodsCount && !totalDropoffMethodsCount) {
        return groupType === 'delivery'
      } else if (totalDropoffMethodsCount && !totalDeliveryMethodsCount) {
        return groupType === 'dropoff'
      }
    }

    return this.selectedMethodGroup === groupType
  }

  private selectedState = (groupType: DeliveryMethodGroupType) =>
    this.isSelected(groupType) ? 'selected' : 'selectable'

  private deliveryGroupCaption = (method: DeliveryMethod) => {
    const { deliveryDate: { date: deliveryDate } } = method
    if (!deliveryDate) { return null }

    const { date, relativeDate: day, diff, weekday } = getRelativeDateInfo(deliveryDate, new Date(), this.currencyContext.locale)

    let pickupDate = `${weekday} ${date}`
    if (diff < 2) {
      pickupDate = `${day} (${weekday})`
    }

    const text = t('home_delivery', { default: 'From {{variable_date}}', ns: 'csc-delivery-match', variable_date: pickupDate })
    return html`${text}`
  }

  renderDeliveryMethodSelectable = () => {
    const firstMethod = this.deliveryMethods[0]

    return html`<cse-selectable
      fit="fill"
      id="delivery-match-group-delivery"
      state="${this.selectedState('delivery')}"
      @click=${(e: Event) => this.handleStepClick(e, 'delivery')}
    >
      ${new CSCDeliveryMatchMethodGroup({
      type: 'delivery',
      caption: firstMethod ? this.deliveryGroupCaption(firstMethod) : null,
      showRadioButton: this.showRadioButtons,
      showIcon: this.showIcons,
    })}
    </cse-selectable>`
  }

  dropoffGroupCaption = () => {
    const method = this.dropoffMethods[0]
    if (!method) return null

    const { deliveryDate: { date: deliveryDate } } = method
    if (deliveryDate === null) return null

    const { date, relativeDate: day, diff, weekday } = getRelativeDateInfo(deliveryDate, new Date(), this.currencyContext.locale)

    let pickupDate = `${weekday} ${date}`
    if (diff < 2) {
      pickupDate = `${day} (${weekday})`
    }

    const text = t('close_delivery_with_time', { default: 'From {{variable_date}}', ns: 'csc-delivery-match', variable_date: pickupDate })
    return html`${text}`
  }

  renderDropoffMethodSelectable = () => {
    return html`<cse-selectable
      fit="fill"
      id="delivery-match-group-dropoff"
      state="${this.selectedState('dropoff')}"
      @click=${(e: Event) => this.handleStepClick(e, 'dropoff')}
    >
      ${new CSCDeliveryMatchMethodGroup({
      type: 'dropoff',
      caption: this.dropoffGroupCaption(),
      showRadioButton: this.showRadioButtons,
      showIcon: this.showIcons,
    })}
    </cse-selectable>`
  }

  renderSkeleton = () => {
    return html`
      <cse-skeleton height="52" index="0"></cse-skeleton>
      <cse-skeleton height="52" index="1"></cse-skeleton>
      <cse-skeleton height="52" index="2"></cse-skeleton>
    `
  }

  renderAlert = () => {
    return html`<cse-alert variant="info">
      ${t('no_delivery_methods_found', { ns: 'csc-delivery-match', default: 'No delivery methods found' })}
    </cse-alert>`
  }

  renderRootError = () => {
    if (!this.error) return nothing
    if (this.error.level == 'method') return nothing

    return html`
      <cse-alert variant=${this.error.severity}>
        ${this.error.title}
        ${this.error.description !== null ? html`
          <span slot="description">
          ${t('generic_error_description', { ns: 'csc-delivery-match', default: 'Something went wrong fetching delivery options. Please try again later.' })}</span>
        ` : nothing}
        
      </cse-alert>
    `
  }

  renderContent = () => {
    const cumulatedMethodCount = this.methodsByCategories.reduce((prev, curr) => prev + curr.methods.length, 0)

    if (this.error && this.error.level == 'root') {
      return this.renderRootError()
    }

    if (this.isLoadingMethods) {
      return this.renderSkeleton()
    } else {
      if (cumulatedMethodCount > 0) {
        return html`
          ${map(this.methodsByCategories, method => {
            if (is<DropoffMethods>(method) && method.methods.length > 0) {
              return this.renderDropoffMethodSelectable()
            }
            if (is<DeliveryMethods>(method) && method.methods.length > 0) {
              return this.renderDeliveryMethodSelectable()
            }

            return nothing
          })}
        `
      } else {
        return this.renderAlert()
      }
    }

  }

  override render() {
    return html`<cse-paper>
      <cse-text variant="heading-3" class="delivery-match-title">
        ${t('delivery_pickup_title', { ns: 'csc-delivery-match', default: 'Delivery & pickup' })}
      </cse-text>
      <div class="${classMap({
        'content': true,
        'inline': this.inline
      })}">
        ${this.renderContent()}
      </div>
    </cse-paper
    >`
  }
}
